import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  PhoneIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerContext } from '../../context/partner-context'
import { useUserContext } from '../../context/user-context'
import {
  AIVY_MEETING_URL,
  BUILD_ENV,
  getAivyOsCustomerUrl,
  sendSlackMessage
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import Modal from '../tailwind/Modal'

const InnovationHelpersModal = ({
  open,
  setOpen,
  trialDays = null,
  source = 'trial_banner',
  calendarUrl = AIVY_MEETING_URL
}) => {
  const { t } = useTranslation()
  const { cognitoUser, activeSpace } = useUserContext()
  const { partner } = usePartnerContext()

  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) return
    // Demo-Buchungs-Funnel (Rock Q3/26): "gezeigt" — die Buchung selbst
    // kommt über den HubSpot-Webhook als Partner.demo_booked_at an.
    trackEvent('demo_booking_widget_shown', { source })
  }, [open, source])

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!phone.trim() || submitting) return
    setSubmitting(true)

    const firstname =
      activeSpace?.person?.firstname ||
      activeSpace?.person?.invitation_firstname ||
      ''
    const lastname =
      activeSpace?.person?.lastname ||
      activeSpace?.person?.invitation_lastname ||
      ''
    const fullName = `${firstname} ${lastname}`.trim() || '—'
    const partnerName = partner?.name || partner?.display_name || '—'
    const email = cognitoUser?.attributes?.email || '—'

    const message = [
      `*${BUILD_ENV.toUpperCase()}*`,
      ':telephone_receiver: *Rückruf-Anfrage (Trial → Innovation Helpers)*',
      `Partner: ${partnerName}`,
      `Person: ${fullName} (${email})`,
      `Telefon: ${phone.trim()}`,
      `Quelle: ${source}`,
      trialDays === null ? null : `Trial-Tage übrig: ${trialDays}`,
      partner?.id ? getAivyOsCustomerUrl({ partner_id: partner.id }) : null
    ]
      .filter(Boolean)
      .join('\n')

    await sendSlackMessage('vertrieb', message)
    trackEvent('demo_callback_requested', { source })
    setSubmitting(false)
    setSubmitted(true)
  }

  const handleClose = (value) => {
    setOpen(value)
    if (!value) {
      setTimeout(() => {
        setPhone('')
        setSubmitted(false)
        setSubmitting(false)
      }, 300)
    }
  }

  return (
    <Modal open={open} setOpen={handleClose} size='xl'>
      <div className='space-y-6'>
        <div className='pr-8'>
          <h2 className='text-xl font-semibold text-slate-900'>
            {t('trial.innovation_helpers.modal.title')}
          </h2>
          <p className='mt-2 text-sm text-slate-600'>
            {trialDays === null
              ? t('trial.innovation_helpers.modal.subtitle_no_trial')
              : t('trial.innovation_helpers.modal.subtitle', {
                  days: trialDays
                })}
          </p>
        </div>

        {/* Beide Wege stehen gleichwertig untereinander und passen ohne
            Scrollen ins Modal — vorher schob das Kalender-Embed den
            Rückruf aus dem Viewport. */}
        <section className='rounded-lg border border-slate-200 p-5'>
          <div className='flex items-center gap-2 text-sm font-medium text-slate-800'>
            <CalendarDaysIcon className='h-5 w-5 shrink-0 text-blue-600' />
            {t('trial.innovation_helpers.modal.calendar_heading')}
          </div>
          <p className='mt-2 text-sm text-slate-600'>
            {t('trial.innovation_helpers.modal.calendar_description')}
          </p>
          <a
            href={calendarUrl}
            target='_blank'
            rel='noreferrer'
            onClick={() => trackEvent('demo_booking_link_clicked', { source })}
            className='mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            {t('trial.innovation_helpers.modal.calendar_action')}
            <ArrowTopRightOnSquareIcon className='h-4 w-4' />
          </a>
        </section>

        <div className='flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400'>
          <div className='h-px flex-1 bg-slate-200' />
          {t('trial.innovation_helpers.modal.divider')}
          <div className='h-px flex-1 bg-slate-200' />
        </div>

        <section className='rounded-lg border border-slate-200 p-5'>
          <div className='flex items-center gap-2 text-sm font-medium text-slate-800'>
            <PhoneIcon className='h-5 w-5 shrink-0 text-blue-600' />
            {t('trial.innovation_helpers.modal.callback_heading')}
          </div>
          <p className='mt-2 text-sm text-slate-600'>
            {t('trial.innovation_helpers.modal.callback_description')}
          </p>

          {submitted ? (
            <div className='mt-4 rounded-md bg-green-50 px-4 py-3 text-sm text-green-700'>
              {t('trial.innovation_helpers.modal.success')}
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className='mt-4 flex flex-col gap-3 sm:flex-row sm:items-end'
            >
              <label className='block flex-1'>
                <span className='text-xs font-medium text-slate-700'>
                  {t('trial.innovation_helpers.modal.phone_label')}
                </span>
                <input
                  type='tel'
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t(
                    'trial.innovation_helpers.modal.phone_placeholder'
                  )}
                  className='mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                  required
                />
              </label>
              <button
                type='submit'
                disabled={submitting}
                className='inline-flex shrink-0 items-center justify-center rounded-md border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {t('trial.innovation_helpers.modal.submit')}
              </button>
            </form>
          )}
        </section>
      </div>
    </Modal>
  )
}

InnovationHelpersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  // null = kein Trial-Kontext (z. B. Onboarding) → Subtitle ohne Tage
  trialDays: PropTypes.number,
  // landet als Property an allen Demo-Events und in der Slack-Nachricht
  source: PropTypes.string,
  calendarUrl: PropTypes.string
}

InnovationHelpersModal.defaultProps = {
  trialDays: null,
  source: 'trial_banner',
  calendarUrl: AIVY_MEETING_URL
}

export default InnovationHelpersModal
